import {
  createFileRoute,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Search,
  Trash2,
  AlertCircle,
  Loader2,
  MapPin,
  Globe,
} from 'lucide-react';

import AdminRoute from '@/components/auth/AdminRoute';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Link } from '@tanstack/react-router';

import {
  useDestinations,
  useDestinationMutations,
} from '@/hooks/useDestinations';
import type { Destination } from '../../../shared/types/seo';

export const Route = createFileRoute('/admin/destinations')({
  component: AdminDestinationsPage,
});

function AdminDestinationsPage() {
  return (
    <AdminRoute>
      <AdminDestinationsContent />
    </AdminRoute>
  );
}

function AdminDestinationsContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContinent, setSelectedContinent] = useState<string>('all');
  const [selectedDestination, setSelectedDestination] =
    useState<Destination | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const routerState = useRouterState();
  const isEditRoute = routerState.location.pathname.includes('/edit');

  const { data: destinations = [], isLoading, error } = useDestinations();

  const { deleteDestination, isDeleting, deleteError } =
    useDestinationMutations();

  // Filter destinations based on search term and continent
  const filteredDestinations = destinations.filter(destination => {
    const matchesSearch =
      searchTerm === '' ||
      destination.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      destination.country.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesContinent =
      selectedContinent === 'all' ||
      destination.continent === selectedContinent;

    return matchesSearch && matchesContinent;
  });

  // Get unique continents for filter
  const continents = Array.from(
    new Set(destinations.map(d => d.continent))
  ).sort();

  const handleDeleteDestination = () => {
    if (selectedDestination) {
      deleteDestination(selectedDestination.id);
      setIsDeleteDialogOpen(false);
      setSelectedDestination(null);
    }
  };

  // If we're on the edit route, render only the Outlet (full-screen)
  if (isEditRoute) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading destinations: {(error as Error).message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Manage Destinations
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage destination data for the travel planning system
            </p>
          </div>
          <Link
            to="/admin/destinations/$destinationId/edit"
            params={{ destinationId: 'new' }}
          >
            <Button>Add Destination</Button>
          </Link>
        </div>

        {/* Error Messages */}
        {deleteError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{deleteError?.message}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search by city or country..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTerm(e.target.value)
                    }
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="md:w-64">
                <Label htmlFor="continent">Continent</Label>
                <Select
                  value={selectedContinent}
                  onValueChange={(value: string) => setSelectedContinent(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select continent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Continents</SelectItem>
                    {continents.map(continent => (
                      <SelectItem key={continent} value={continent}>
                        {continent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDestinations.map(destination => (
            <motion.div
              key={destination.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">
                        {destination.city}
                      </CardTitle>
                      <CardDescription className="flex items-center mt-1">
                        <MapPin className="h-4 w-4 mr-1" />
                        {destination.country}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">{destination.continent}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="text-xs">
                      <Globe className="h-3 w-3 mr-1" />
                      {destination.airportCodes.join(', ')}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {destination.tourism.currency}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {destination.seo.metaDescription}
                  </p>
                  <div className="flex space-x-2">
                    <Link
                      to="/admin/destinations/$destinationId/edit"
                      params={{ destinationId: destination.id }}
                    >
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedDestination(destination);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredDestinations.length === 0 && destinations.length > 0 && (
          <Card className="text-center py-8">
            <CardContent>
              <p className="text-muted-foreground">
                No destinations match your search criteria
              </p>
            </CardContent>
          </Card>
        )}

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Destination</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedDestination?.city}"?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setSelectedDestination(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteDestination}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
